import { Injectable, effect, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { IdbStorageService, IDB_STORE_SETTINGS } from './idb-storage.service';
import { App } from '@capacitor/app';
import { environment } from '../../../environments/environment';
import { appSettingsToModel } from '../api/adapters';
import { AppSettings } from '../models/settings.model';
import { AuthService } from './auth.service';
import { SentryService } from './sentry.service';
import { VersionCheckService, type VersionCheckResult } from './version-check.service';
import packageInfo from '../../../../package.json';

/** SecureStorage key for the cached app settings (not entity-scoped — global to the device). */
const CACHE_KEY = 'app_settings_cache';

/**
 * Fetches and exposes application-level settings from `GET /settings/app`.
 *
 * @remarks
 * **Startup:** `restore()` is called via `APP_INITIALIZER` and reads the cached
 * value from SecureStorage so settings are available before the first navigation,
 * without requiring a token.
 *
 * **After login:** an `effect()` watches `AuthService.isAuthenticated()` and
 * calls `fetchAndCache()` once a Bearer token is available, guaranteeing fresh
 * settings are in place before `JobsService` and other post-login API calls run.
 *
 * **Manual refresh:** `SyncService.manualRefresh()` (the header's refresh-icon
 * tap) calls the public {@link refresh} method so a manual "force refresh"
 * also re-fetches `/settings/app` — otherwise settings would only ever
 * update once at boot and once right after login, for the rest of the
 * session (2026-07-28). Auto-sync and login's own `fullSync()` do **not**
 * call it — only the explicit manual refresh does, per the same scoping as
 * the request that added this.
 *
 * **Cache key:** `app_settings_cache` — not entity-scoped because settings are
 * global to the app, not per-user. The entity ID is unavailable before login.
 *
 * Consumers read the `settings` signal; `null` means no cache and no successful
 * fetch has completed yet.
 */
@Injectable({ providedIn: 'root' })
export class AppSettingsService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly versionCheckSvc = inject(VersionCheckService);
  private readonly sentry = inject(SentryService);
  private readonly idb = inject(IdbStorageService);

  /** Resolved app settings, or `null` if not yet loaded or load failed. */
  readonly settings = signal<AppSettings | null>(null);

  /**
   * Semantic app version for comparisons (e.g. "1.0.11").
   * Always from package.json — used by VersionCheckService.isVersionOutdated().
   * Does NOT include build number so version comparison works correctly.
   */
  readonly appVersion: string = packageInfo.version as string;

  /**
   * Full app version for display: `{versionName}-{versionCode}` on native
   * (e.g. "1.0.11-73"), or `{package.json version}` on web (e.g. "1.0.11").
   * Initialized async on startup from `App.getInfo()` with fallback to package.json.
   */
  displayVersion: string = packageInfo.version as string;

  /** Result of version check against server policy (`null` if settings not yet loaded). */
  readonly versionCheck = computed<VersionCheckResult | null>(() => {
    const settings = this.settings();
    if (!settings) return null;
    return this.versionCheckSvc.checkForUpdate(this.appVersion, settings);
  });

  /**
   * Resolves once the first post-login settings fetch has settled (success or fail).
   * Callers such as `JobsService` await this before making their own API calls.
   */
  readonly loaded: Promise<void>;
  private resolveLoaded!: () => void;

  /**
   * `true` once `init()`'s first `fetchAndCache()` attempt has settled
   * (success or failure) — a signal counterpart to {@link loaded} for
   * template use (`App`'s splash screen needs a reactive value, not a
   * `Promise`).
   *
   * @remarks
   * **Only relevant on a brand-new install with no cached settings yet**
   * (2026-07-29, added alongside the fix that stopped blocking app
   * bootstrap on this fetch — see `init()`'s doc comment). `App`'s root
   * template shows a lightweight splash (`[App Logo] / Loading…`) while
   * `settings()` is still `null` **and** this is `false` — i.e. only during
   * the narrow window before the very first settings fetch has ever
   * completed on this device. A returning device with a populated
   * `app_settings_cache` never sees it at all, since `settings()` is
   * already non-null immediately from `restore()`.
   */
  readonly initialLoadSettled = signal(false);

  constructor() {
    this.loaded = new Promise<void>(resolve => { this.resolveLoaded = resolve; });

    // Initialize display version (with build number) from Capacitor on native.
    void this.initDisplayVersion();

    // Post-login silent refresh: re-fetch with Bearer token so settings stay
    // fresh for the session — but skip the very first evaluation of this
    // effect if the app boots already-authenticated (a reload with a valid
    // stored token). `init()`'s own background fetch (see its doc comment)
    // already covers that case; without this guard, `AuthService`
    // restoring a valid token at boot made `isAuthenticated()` true almost
    // immediately, firing THIS effect's fetch at essentially the same
    // moment as `init()`'s — two near-simultaneous GET /settings/app calls
    // on every reload of an already-logged-in session (reported live via
    // the Network tab, 2026-07-29). A genuine new login later in the same
    // session (isAuthenticated() transitioning false→true after boot) is
    // NOT skipped — that's a real event this refresh is meant to react to.
    let isFirstEvaluation = true;
    effect(() => {
      const authenticated = this.auth.isAuthenticated();
      if (isFirstEvaluation) {
        isFirstEvaluation = false;
        if (authenticated) return;
      }
      if (authenticated) {
        void this.fetchAndCache();
      }
    });
  }

  /**
   * Initializes displayVersion from native Capacitor API or falls back to appVersion.
   *
   * @remarks
   * **Native (iOS/Android):** `App.getInfo()` returns `versionName` and `versionCode`;
   * formatted as `{versionName}-{versionCode}` (e.g. "1.0.11-73").
   *
   * **Web/browser:** `App.getInfo()` rejects; falls back to appVersion (semantic only)
   * (e.g. "1.0.11").
   *
   * **appVersion is always semantic** (from package.json) for version comparisons.
   *
   * Runs in constructor, fire-and-forget — does not block app initialization.
   */
  private async initDisplayVersion(): Promise<void> {
    try {
      const info = await App.getInfo();
      this.displayVersion = `${info.version}-${info.build}`;
    } catch {
      // Web/browser fallback — already set in constructor with package.json version
    }
  }

  /**
   * Called by `APP_INITIALIZER` on every app launch.
   *
   * @remarks
   * **Only `restore()` blocks app bootstrap (2026-07-29 fix)** — reading the
   * cached settings from SecureStorage is fast and synchronous-ish (no
   * network round trip), so the app renders immediately using whatever was
   * last cached (or the field defaults, on a first-ever launch). The actual
   * `fetchAndCache()` network call (`GET /settings/app`) runs in the
   * background afterward, fire-and-forget from `init()`'s own perspective.
   *
   * **Previously `init()` awaited `fetchAndCache()` too, and `APP_INITIALIZER`
   * blocks Angular's first render until every initializer's promise
   * resolves** — so a slow/cold `/settings/app` response (this call is
   * unauthenticated and runs before login, so it has no cached Bearer-token
   * fast path) meant the entire app stayed a blank white screen for however
   * long that request took, with zero visual feedback. Reported live. Fixed
   * by no longer awaiting the fetch inside `init()` itself — `loaded` still
   * resolves only once the fetch actually settles (`fetchAndCache().finally(...)`),
   * so `JobsService` (which awaits `appSettingsService.loaded` before its own
   * first fetch) is unaffected and still gets fresh settings before it needs
   * them; only the *initial paint* no longer waits on this network call.
   *
   * @returns A promise that always resolves (never rejects) once `restore()`
   *   settles — does not wait for the background fetch.
   */
  async init(): Promise<void> {
    await this.restore();
    void this.fetchAndCache().finally(() => {
      this.resolveLoaded();
      this.initialLoadSettled.set(true);
    });
  }

  /**
   * Restores cached settings from SecureStorage.
   *
   * @remarks
   * Reads from IndexedDB, migrating a legacy SecureStorage entry on first run
   * (2026-08-05) — see `IdbStorageService.getOrMigrate()`. An absent key yields
   * `null`, which is left as a cache miss.
   *
   * @returns A promise that always resolves (never rejects).
   */
  async restore(): Promise<void> {
    const cached = await this.idb.getOrMigrate<AppSettings>(
      IDB_STORE_SETTINGS,
      CACHE_KEY,
      (raw) => JSON.parse(raw) as AppSettings,
    );
    if (cached) this.settings.set(cached);
  }

  /**
   * Re-fetches app settings from `GET /settings/app` and updates {@link settings}.
   *
   * @remarks
   * Public entry point for an explicit, on-demand refresh — currently only
   * called by `SyncService.manualRefresh()` so tapping the header's refresh
   * icon also picks up a changed setting (e.g. `print_packet_threshold_web`)
   * without waiting for the next login. A thin wrapper around
   * {@link fetchAndCache}, which already swallows its own errors.
   */
  async refresh(): Promise<void> {
    await this.fetchAndCache();
  }

  /**
   * Fetches app settings from the API and persists the result to IndexedDB.
   *
   * @remarks
   * Called from `init()` on startup (no auth), from the post-login
   * `effect()` with a Bearer token, and from the public {@link refresh}
   * method. Errors are swallowed so a failure never blocks the app; the
   * cached value (if any) remains active.
   */
  private async fetchAndCache(): Promise<void> {
    try {
      const raw = await firstValueFrom(
        this.http.get<unknown>(`${environment.apiUrl}/settings/app`)
      );
      const settings = appSettingsToModel(raw);
      this.settings.set(settings);
      await this.idb.setOrFallback(IDB_STORE_SETTINGS, CACHE_KEY, settings, () => JSON.stringify(settings));
    } catch (err) {
      // Non-200 responses (including 409 from UAT) are non-critical — cached
      // value (if any) remains active. refreshIntervalMs defaults to 60 000 ms.
      this.sentry.captureException(err, { tags: { source: 'app-settings' } });
      console.warn('[AppSettings] fetch failed', err);
    }
  }
}
